import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { hasPermission } from '../utils/auth';
import './DashboardLayout.css';

function DashboardLayout({ onLogout, user }) {
  const [showReportsSubMenu, setShowReportsSubMenu] = useState(false);
  const [showConfigSubMenu, setShowConfigSubMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/reports')) setShowReportsSubMenu(true);
    if (location.pathname.startsWith('/config'))  setShowConfigSubMenu(true);
  }, [location.pathname]);

  // DASHBOARD
  const showDashboard    = hasPermission('dashboard_full') || hasPermission('dashboard_individual');
  const dashboardPath    = hasPermission('dashboard_full') ? '/dashboard' : '/dashboard-individual';

  // DATA ENTRY
  const showDataEntry    = hasPermission('data_entry');

  // INGRESO EXPRESS
  const showIngresoExpress = hasPermission('ingreso_express');

  // RETROSPECTIVE
  const showRetrospective = hasPermission('retrospective');

  // REPORTS
  const showReports        = hasPermission('report_clasico') || hasPermission('report_kpi');
  const showReporteClasico = hasPermission('report_clasico');
  const showReporteKpi     = hasPermission('report_kpi');

  // CONFIG
  const showConfigUsers    = hasPermission('config_users');
  const showConfigHotels   = hasPermission('config_hotels');
  const showConfigCompsets = hasPermission('config_compsets');
  const showConfigImages   = hasPermission('config_images');
  const showConfigRoles    = hasPermission('config_roles');
  const showConfig         = showConfigUsers || showConfigHotels || showConfigCompsets || showConfigImages || showConfigRoles;

  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);

  return (
    <div className="dashboard-layout">
      <button className="hamburger-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
        <span className={`bar ${isMobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`bar ${isMobileMenuOpen ? 'open' : ''}`}></span>
        <span className={`bar ${isMobileMenuOpen ? 'open' : ''}`}></span>
      </button>

      <aside className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="logo-section">
          <img src={`${process.env.PUBLIC_URL}/riberana_logo.png`} alt="Riberana Logo" className="sidebar-logo" />
        </div>

        <nav className="menu-nav">
          <ul>
            {/* DASHBOARD */}
            {showDashboard && (
              <li className={`menu-item ${location.pathname === dashboardPath ? 'active' : ''}`} onClick={() => navigate(dashboardPath)}>
                <span className="icon">🏠</span> Dashboard
              </li>
            )}

            {/* INGRESO DE INFO */}
            {showDataEntry && (
              <li className={`menu-item ${location.pathname === '/data-entry' ? 'active' : ''}`} onClick={() => navigate('/data-entry')}>
                <span className="icon">📄</span> Ingreso de info
              </li>
            )}

            {/* INGRESO EXPRESS */}
            {showIngresoExpress && (
              <li className={`menu-item ${location.pathname === '/ingreso-express' ? 'active' : ''}`} onClick={() => navigate('/ingreso-express')}>
                <span className="icon">⚡</span> Ingreso Express
              </li>
            )}

            {/* INGRESO RETROSPECTIVO */}
            {showRetrospective && (
              <li className={`menu-item ${location.pathname === '/ingreso-retrospectivo' ? 'active' : ''}`} onClick={() => navigate('/ingreso-retrospectivo')}>
                <span className="icon">🕰️</span> Ingreso Retrospectivo
              </li>
            )}

            {/* REPORTS */}
            {showReports && (
              <>
                <li
                  className={`menu-item ${location.pathname.startsWith('/reports') || showReportsSubMenu ? 'active expanded' : ''}`}
                  onClick={() => setShowReportsSubMenu(!showReportsSubMenu)}
                >
                  <span className="icon">📊</span> Reportes
                  <span className="toggle-icon">{showReportsSubMenu ? '▼' : '►'}</span>
                </li>
                {showReportsSubMenu && (
                  <ul className="submenu">
                    {showReporteClasico && (
                      <li className={`submenu-item ${location.pathname === '/reports/clasico' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/reports/clasico'); }}>
                        Reporte Clásico
                      </li>
                    )}
                    {showReporteKpi && (
                      <li className={`submenu-item ${location.pathname === '/reports/kpi' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/reports/kpi'); }}>
                        Reporte de KPIs
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* CONFIGURATION */}
            {showConfig && (
              <>
                <li
                  className={`menu-item ${location.pathname.startsWith('/config') || showConfigSubMenu ? 'active expanded' : ''}`}
                  onClick={() => setShowConfigSubMenu(!showConfigSubMenu)}
                >
                  <span className="icon">⚙️</span> Configuración
                  <span className="toggle-icon">{showConfigSubMenu ? '▼' : '►'}</span>
                </li>
                {showConfigSubMenu && (
                  <ul className="submenu">
                    {showConfigUsers && (
                      <li className={`submenu-item ${location.pathname === '/config/users' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/config/users'); }}>
                        Usuarios
                      </li>
                    )}
                    {showConfigHotels && (
                      <li className={`submenu-item ${location.pathname === '/config/hotels' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/config/hotels'); }}>
                        Hoteles
                      </li>
                    )}
                    {showConfigCompsets && (
                      <li className={`submenu-item ${location.pathname === '/config/compsets' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/config/compsets'); }}>
                        Comp Sets
                      </li>
                    )}
                    {showConfigImages && (
                      <li className={`submenu-item ${location.pathname === '/config/images' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/config/images'); }}>
                        Gestión de Imágenes
                      </li>
                    )}
                    {showConfigRoles && (
                      <li className={`submenu-item ${location.pathname === '/config/roles' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); navigate('/config/roles'); }}>
                        Papeles
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* LOGOUT */}
            <li className="menu-item" onClick={onLogout}>
              <span className="icon">🚪</span> Cerrar Sesión
            </li>
          </ul>
        </nav>
      </aside>

      {isMobileMenuOpen && <div className="mobile-overlay" onClick={toggleMobileMenu} />}

      <main className="main-content">
        <header className="main-content-header">
          {user && (
            <span className="user-info">Logged in as: {user.name} ({user.user_level})</span>
          )}
        </header>
        <Outlet />
      </main>
    </div>
  );
}

export default DashboardLayout;